import Swal from 'sweetalert2'
import { DELETE, GET, POST } from '../../../../../../services/api'
import {
  FilterProduct,
  ProductMappingType,
  TableParams,
  ProductTypeResponseApi,
  FilterProductDto,
} from '../type'
import { useState, useEffect } from 'react'

import { productMapper } from './productMapper'
import usePluginsSpace from '../../../../../../hooks/usePluginSpace'
import {
  getTableConfig,
  saveTableConfig,
} from '../../../../../../components/ModularDataTable/utils/persistConfig'

export function useProduct(persistPageKey_?: string, persistContextKey_?: string) {
  // Defina as chaves de persistência (pode ser dinâmico se quiser)
  const persistPageKey = persistPageKey_ || 'produtos'
  const persistContextKey = persistContextKey_ || 'default'

  const { pluginSpace } = usePluginsSpace()
  const isWholeSaler = Boolean(pluginSpace?.wholeSaler)

  const [products, setProducts] = useState<ProductMappingType[]>([])
  const [loading, setLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [productId, setProductId] = useState<number | null>(null)

  // Carrega persistido
  const persisted = getTableConfig(persistPageKey, persistContextKey) || {}

  const [pagination, setPagination] = useState({
    page: persisted.pagination?.page || 1,
    pageSize: persisted.pagination?.pageSize || 10,
    total: 0,
    pageSizes: [10, 25, 50, 100],
  })
  const [sorting, setSorting] = useState({
    field: persisted.sorting?.field || 'id',
    direction: persisted.sorting?.direction || 'asc',
  })
  const [searchTerm, setSearchTerm] = useState(persisted.filters?.search || '')
  const [productFilters, setProductFilters] = useState<FilterProductDto>(persisted.filters || {})

  const [categories, setCategories] = useState<any[]>([])
  // Remover estado separado para filterGroup
  const [registeredGroups, setRegisteredGroups] = useState([])

  const handleFilterChange = async (newFilters: FilterProductDto) => {
    setProductFilters(newFilters)
  }

  const handleDataChange = async (params: {
    page: number
    pageSize: number
    search: string
    sorting?: { field: string; direction: 'asc' | 'desc' }
    filters?: any
  }) => {
    setPagination((prev) => ({ ...prev, page: params.page, pageSize: params.pageSize }))
    if (params.sorting) setSorting(params.sorting)
    setSearchTerm(params.search)
    if (params.filters) setProductFilters(params.filters)
    return await getProducts(params)
  }

  // Sempre que filtros, paginação ou sorting mudarem, salva tudo junto
  useEffect(() => {
    saveTableConfig(persistPageKey, persistContextKey, {
      filters: productFilters,
      pagination,
      sorting,
    })
  }, [productFilters, pagination, sorting])

  const handlePaginationChange = async (newPagination: any) => {
    setPagination(newPagination)
    return await getProducts({
      page: newPagination.page,
      pageSize: newPagination.pageSize,
      search: searchTerm,
      sorting: sorting.field ? sorting : undefined,
    })
  }

  const handleSortingChange = async (newSorting: any) => {
    setSorting(newSorting)
   return await getProducts({
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: searchTerm,
      sorting: newSorting,
    })
  }

  function translateFilter(data: FilterProductDto): FilterProduct {
    const status =
      !data.status || data.status?.value === 2 ? undefined : data.status?.value === 1 ? true : false
    const classificationId =
      !data.classificationId || data.classificationId?.value === 0
        ? undefined
        : data.classificationId.value

    let noGroup = undefined
    let customerGroupId = undefined

    // Usar data.filterGroup ao invés de filterGroup do estado
    if (isWholeSaler && data.filterGroup && data.filterGroup.length > 0) {
      const selectedValues = data.filterGroup.map((g: any) => g.value ?? g)
      if (selectedValues.includes(-1)) {
        noGroup = true
      }
      const groupIds = selectedValues.filter((v: number) => v > 0)
      if (groupIds.length > 0) {
        customerGroupId = groupIds
      }
    }

    return {
      classificationId: classificationId?.toString(),
      status,
      noGroup: noGroup,
      customerGroupId: customerGroupId,
    }
  }

  async function getProducts(params?: TableParams) {
    try {
      setTableLoading(true)
      const currentPage = params?.page || pagination.page
      const currentPageSize = params?.pageSize || pagination.pageSize
      const currentSearch = params?.search || searchTerm
      const currentSorting = params?.sorting || sorting

      const query = `?field=${currentSorting.field
        }&order=${currentSorting.direction.toUpperCase()}&page=${currentPage}&take=${currentPageSize}&search=${currentSearch}`
      const currentFilters = translateFilter(params?.filters ?? productFilters)

      console.log('[getProducts] Query:', query)
      console.log('[getProducts] Filtros:', currentFilters)

      const request = await POST(`/product/filter_admin_fashion${query}`, currentFilters)
      console.log('[getProducts] Resposta da API:', request)
      if (!request.data) {
        console.warn('[getProducts] Nenhum dado retornado da API!')
      }
      const mappedProducts =
        request.data?.map((item: ProductTypeResponseApi) => productMapper(item)) || []
      setProducts(mappedProducts)
      console.log('[getProducts] Produtos mapeados:', mappedProducts)
      if (request.meta) {
        const paginagionInfo = {
          total: request.meta.itemCount,
          page: currentPage,
          pageSize: currentPageSize,
        }
        setPagination((prev) => ({
          ...prev,
          ...paginagionInfo,
        }))
        console.log('[getProducts] Paginação atualizada:', paginagionInfo)
      }
      return mappedProducts
    } catch (error) {
      console.log('[getProducts] Erro:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Um erro ocorreu ao filtrar os produto',
      })
      return []
    } finally {
      setTableLoading(false)
    }
  }

  async function getCategories() {
    try {
      setLoading(true)
      const resCategorias = await POST(`/classification/filter`)
      console.log('[getCategories] Resposta da API:', resCategorias)

      const categories = [
        {
          value: 0,
          select: 'TODAS',
        },
        ...(resCategorias.data?.map((el: any) => {
          return {
            value: el.id,
            select: el.name,
          }
        }) ?? []),
      ]

      categories.sort((a: any, b: any) => {
        if (a.value === 0) return -1
        if (b.value === 0) return 1

        return a.select.toUpperCase() > b.select.toUpperCase()
          ? 1
          : a.select.toUpperCase() < b.select.toUpperCase()
            ? -1
            : 0
      })
      setCategories(categories)
      console.log('[getCategories] Categorias salvas:', categories)
    } catch (error) {
      console.log('[getCategories] Erro:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Um erro ocorreu ao buscar as categorias',
      })
    } finally {
      setLoading(false)
    }
  }

  async function getCustomersGroups() {
    try {
      const res = await GET('/customer_groups')
      console.log('[getCustomersGroups] Resposta da API:', res)
      if (res && res.data) {
        setRegisteredGroups(res.data)
        console.log('[getCustomersGroups] registeredGroups salvos:', res.data)
      } else {
        console.warn('[getCustomersGroups] Nenhum grupo retornado!')
      }
    } catch (error) {
      console.error('[getCustomersGroups] Erro:', error)
    }
  }

  const deleteProduct = async () => {
    try {
      setLoading(true)
      const markRes = await DELETE(`/product/${productId}`)
      if (markRes.success === true) {
        Swal.fire({
          icon: 'success',
          title: 'Produto deletado',
          text: 'Deleção realizada com sucesso!',
        }).then(async () => await getProducts())
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao deletar o produto!',
        }).then(async () => await getProducts())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao deletar o produto!',
      }).then(async () => await getProducts())
      console.log(e)
    }
  }

  return {
    products,
    loading,
    tableLoading,
    pagination,
    sorting,
    searchTerm,
    productFilters,
    getProducts,
    setSorting,
    setSearchTerm,
    setProductFilters,
    handleDataChange,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
    setProductId,
    categories,
    registeredGroups,
    setTableLoading,
    getCategories,
    getCustomersGroups,
    setLoading,
    deleteProduct,
  }
}
